import { EVENT } from "@/constants/event";
import { RENDERCARDTYPE } from "@/constants/renderCardType";
import { isSafari } from "@/utils/isSafari";
import { useEventListener, useMemoizedFn, usePrevious } from "ahooks";
import classNames from "classnames";
import { debounce } from "lodash";
import Script from "next/script";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import styles from "./index.module.less";
import { useDraggable } from "./useDraggable";

const MAX_HEIGHT_DIFF = 80;

// 抽取填充逻辑的通用函数

export default forwardRef(
  /**
   * 瀑布流
   * @param {*} props
   */
  function MasonryWithBanner(
    {
      brickId = "id",
      bricks = [],
      render,
      gutter = 16,
      columnSize = 240,
      columnNum = 4,
      children,
      threshold = 1, // 预加载的滚动区域，小于10是容器倍数，大于10是绝对值
      scrollElement, // 滚动元素
      className = "masonry",
      brickClassName = "brick",
      masonryKey = "masonryKey", // 切换不同的瀑布流实例，用于，重新计算 containerOffsetTop 值
      onRendered = () => {},
      loadingContent = null,
      // getBrickHeight,
      onDragSortEnd,
      enableDragSort,
      leftOffset = 0, // 左偏移量，用于居中对齐
      style = {}, // 样式
    },
    ref
  ) {
    const containerRef = useRef(null);
    const computedBricks = useRef(new Map()); // 已经计算大小和位置的bricks
    const fillBricksMap = useRef(new Map()); // 单独的填充元素map，不影响其他元素
    const columnHeightArr = useRef([]); // 瀑布流各列高度
    const containerScrollHeight = useRef(0);
    const [containerOffsetTop, setContainerOffsetTop] = useState(0); // 瀑布流容器位置
    const [containerHeight, setContainerHeight] = useState(0); // 瀑布流容器高度
    const [scrollTop, setScrollTop] = useState(0); // 页面滚动高度
    const isUnmount = useRef(false); // 是否销毁
    const loadingRef = useRef(true);
    const prevBricks = usePrevious(bricks);

    useMemo(() => {
      if (prevBricks?.length > bricks.length) {
        // 当瀑布流重置时，需要重置高度缓存
        containerScrollHeight.current = 0;
      }
    }, [bricks, prevBricks]);

    useEffect(() => {
      isUnmount.current = false;
      return () => {
        isUnmount.current = true;
      };
    }, []);

    const { isDragging, handleDragStart } = useDraggable({ onDragSortEnd });

    // 找到高度小于但最接近指定高度差的 brick
    const findBrickToFillGap = useCallback(
      (heightDiff, bricks, excludeIndex, brickToDomMap, usedFillBricks) => {
        let bestBrick = null;
        let closestDiff = Infinity;

        for (let i = 0; i < bricks.length; i++) {
          // 跳过当前 banner 元素和已经处理过的元素
          if (i === excludeIndex) continue;

          const brick = bricks[i];

          // 跳过特殊类型的卡片，只选择普通卡片作为填充
          if (
            brick.type &&
            Object.values(RENDERCARDTYPE).includes(brick.type)
          ) {
            continue;
          }

          // 跳过已经被用作 fillBrick 的元素
          if (usedFillBricks.has(brick[brickId])) {
            continue;
          }

          let brickHeight = Infinity;
          const domElement = brickToDomMap.get(brick[brickId]);

          if (domElement) {
            brickHeight = Math.floor(domElement.clientHeight);
          }

          // 检查 brick 高度是否小于高度差
          if (brickHeight <= heightDiff) {
            // 计算与目标高度差的差距
            const diff = heightDiff - brickHeight;
            if (diff < closestDiff) {
              closestDiff = diff;
              bestBrick = {
                index: i,
                brick: brick,
                height: brickHeight,
              };
            }
          }
        }

        return bestBrick;
      },
      [brickId, bricks]
    );

    // 创建填充逻辑的通用函数
    const createFillBrickLogic = useCallback(
      ({
        brickId,
        columnSize,
        gutter,
        maxColumn,
        columnHeightArrCurrent,
        bricks,
        brickToDomMap,
        clientHeight,
        minHeight,
        mergeCol,
        excludeIndex,
      }) => {
        const usedFillBricks = new Set();

        for (let i = maxColumn - 1; i >= maxColumn - mergeCol; i--) {
          const heightDiff = minHeight - columnHeightArrCurrent[i];
          if (heightDiff > MAX_HEIGHT_DIFF) {
            // 直接计算填充元素，不再使用缓存
            let fillBrick = null;
            if (brickToDomMap) {
              fillBrick = findBrickToFillGap(
                heightDiff - gutter,
                bricks,
                excludeIndex,
                brickToDomMap,
                usedFillBricks
              );
            } else {
              // 如果没有 brickToDomMap，跳过重新计算
              continue;
            }

            if (fillBrick) {
              const fillBrickId = `${fillBrick.brick[brickId]}_fill_${i}`;

              const fillBrickRect = {
                column: i,
                left: i * (columnSize + gutter),
                top: columnHeightArrCurrent[i],
                height: fillBrick.height,
                width: columnSize,
                bottom: columnHeightArrCurrent[i] + fillBrick.height,
                right: i * (columnSize + gutter) + columnSize,
                isBanner: fillBrick.brick.isBanner,
                isFillBrick: true,
                originalBrickId: fillBrick.brick[brickId],
                mergeCol: 1,
              };

              // 将填充元素添加到单独的map中，不影响computedBrickMap
              fillBricksMap.current.set(fillBrickId, fillBrickRect);
              usedFillBricks.add(fillBrick.brick[brickId]);
            }
          }

          columnHeightArrCurrent[i] =
            minHeight + (clientHeight > 0 ? clientHeight + gutter : 0);
        }
      },
      [MAX_HEIGHT_DIFF, fillBricksMap, findBrickToFillGap]
    );

    // 移除填充元素缓存相关的函数

    const renderChild = useMemo(() => {
      const childrenNode = React.Children.toArray(children);
      return function Fn(record, index) {
        const computedBrickMap = computedBricks.current;
        const fillBricksMapRef = fillBricksMap.current;

        let props = {
          className: styles.brick,
          draggable: false,
          // "data-key": record[brickId],
        };

        if (enableDragSort && !record?.disableDrag) {
          props.onMouseDown = (e) => handleDragStart(e, record);
          props["data-mark"] = record[brickId];
        }

        // 检查是否是填充元素
        if (record.isFillBrick && fillBricksMapRef.has(record[brickId])) {
          const rect = fillBricksMapRef.get(record[brickId]);
          const style = {
            transform: `translate(${rect.left}px, ${rect.top}px)`,
          };

          // 如果有 mergeCol，设置宽度
          if (rect.mergeCol && rect.mergeCol > 1) {
            style.width = `${rect.width}px`;
          }

          props = {
            ...props,
            className: classNames(
              styles.brick,
              styles.show,
              brickClassName,
              styles.fillBrick
            ),
            style,
          };
        } else if (computedBrickMap.has(record[brickId])) {
          const rect = computedBrickMap.get(record[brickId]);
          const style = {
            transform: `translate(${rect.left}px, ${rect.top}px)`,
          };

          // 如果有 mergeCol，设置宽度
          if (rect.mergeCol && rect.mergeCol > 1) {
            style.width = `${rect.width}px`;
          }

          props = {
            ...props,
            className: classNames(styles.brick, styles.show, brickClassName),
            style,
          };
        }

        // 先渲染 组件子元素
        if (record.mansonryInnerChildIndex !== undefined) {
          return (
            <div key={record.mansonryInnerChildIndex} {...props}>
              {childrenNode[record.mansonryInnerChildIndex]}
            </div>
          );
        }

        // 对于填充元素，确保传递给 render 函数的 record 包含正确的 pin_id
        const renderRecord =
          record.isFillBrick && record.originalPinId
            ? { ...record, [brickId]: record.originalPinId }
            : record;

        return (
          <div key={record[brickId]} {...props}>
            {render(renderRecord, index)}
          </div>
        );
      };
    }, [
      brickId,
      children,
      render,
      brickClassName,
      handleDragStart,
      enableDragSort,
    ]);

    // 获取瀑布流容器位置
    useEffect(() => {
      const getContainerOffsetTop = () => {
        const realScrollElement =
          typeof scrollElement === "string"
            ? document.getElementById(scrollElement)
            : scrollElement || document.documentElement;

        const containerRect = containerRef.current?.getBoundingClientRect();
        const containerRectTop = containerRect?.top;

        if (!scrollElement) {
          setContainerOffsetTop(containerRectTop + window.scrollY);
        } else {
          setContainerOffsetTop(
            containerRectTop -
              realScrollElement?.getBoundingClientRect()?.top +
              realScrollElement.scrollTop
          );
        }
      };

      // 立即执行一次
      getContainerOffsetTop();

      // Safari浏览器问题，没有延迟获取，他会读取一个错误的异常值，需要异步再读取一下
      if (isSafari()) {
        let rafId = requestAnimationFrame(getContainerOffsetTop);
        return () => {
          cancelAnimationFrame(rafId);
        };
      }
    }, [scrollElement, masonryKey]);

    // 获取瀑布流容器高度
    const getContainerDimensions = useCallback(() => {
      // 真实滚动元素
      const realScrollElement =
        typeof scrollElement === "string"
          ? document.getElementById(scrollElement)
          : scrollElement || document.documentElement;
      const containerHeight = realScrollElement?.clientHeight || 0;
      setContainerHeight(containerHeight);
    }, [scrollElement]);

    useEffect(() => {
      getContainerDimensions();
      window.addEventListener("resize", getContainerDimensions);
      return () => {
        window.removeEventListener("resize", getContainerDimensions);
      };
    }, [getContainerDimensions]);

    // 所有要渲染的元素：子元素+列表
    const allBricks = useMemo(() => {
      let list = [];
      React.Children.map(children, (child, index) => {
        if (child) {
          list.push({
            mansonryInnerChildIndex: index,
            [brickId]: `childId_${index}`,
          });
        }
      });
      list.push(...bricks);
      return list;
    }, [brickId, bricks, children]);

    // 顶部加载新数据 或 删除某个brick
    useEffect(() => {
      const bricks = allBricks;

      // 清除bricks
      if (!bricks.length) {
        columnHeightArr.current.fill(0);
        computedBricks.current.clear();
        fillBricksMap.current.clear();
        return;
      }

      const keys = {};
      let index = 0;
      for (let [key] of computedBricks.current) {
        let realKey = bricks[index] && bricks[index][brickId];

        // 循环跳过重复的brick
        while (keys[realKey] && index < bricks.length - 1) {
          index += 1;
          realKey = bricks[index][brickId];
        }

        if (!bricks[index] || bricks[index][brickId] !== key) {
          // 变更出现在前面几个，直接全部清空
          if (index < 5) {
            columnHeightArr.current.fill(0);
            computedBricks.current.clear();
            fillBricksMap.current.clear(); // 清空填充元素map
            break;
          }

          // 清除当前brick后的所有brick缓存
          let clearAfter = false;
          for (let [remainKey, remainBrick] of computedBricks.current) {
            if (clearAfter || remainKey === key) {
              clearAfter = true;

              computedBricks.current.delete(remainKey);
              columnHeightArr.current[remainBrick.column] = Math.min(
                remainBrick.top,
                columnHeightArr.current[remainBrick.column]
              );
            }
          }
          break;
        }

        keys[key] = true;
        index += 1;
      }
    }, [allBricks, brickId]);

    const recompute = useMemoizedFn(() => {
      // 重排后每列高度会变化，需要清空
      // 确保 columnHeightArr 的长度与 columnNum 一致
      if (columnHeightArr.current.length !== columnNum) {
        columnHeightArr.current = Array(columnNum).fill(0);
      } else {
        columnHeightArr.current.fill(0);
      }

      const columnHeightArrCurrent = columnHeightArr.current;
      const computedBrickMap = computedBricks.current;
      const maxColumn = columnNum;

      const newMap = new Map();
      // 保持填充元素稳定，不清空缓存

      // 遍历allBricks,根据缓存的内容，重新建立高度
      for (let index = 0; index < allBricks.length; index++) {
        const key = allBricks[index]?.[brickId];
        const rect = computedBrickMap.get(key);
        if (rect) {
          let minHeight = Infinity;
          let columNum = 0;

          const isBanner = allBricks[index]?.isBanner;
          const isFirstColumn = allBricks[index]?.isFirstColumn;

          if (isBanner && isFirstColumn) {
            // 【同在画板】等特殊Banner：第一列插入，但使用Banner的高度逻辑避免重叠
            minHeight = Math.max(...columnHeightArrCurrent);
            columNum = 0;
          } else if (isBanner) {
            // 普通Banner：从最右边开始
            minHeight = Math.max(...columnHeightArrCurrent);
            columNum = maxColumn - allBricks[index]?.mergeCol;
          } else if (isFirstColumn) {
            // 普通卡片强制插入到第一列
            columNum = 0;
            minHeight = columnHeightArrCurrent[0];
          } else {
            // 普通卡片：获取最小的列
            columnHeightArrCurrent.forEach((value, i) => {
              if (value < minHeight) {
                columNum = i;
                minHeight = value;
              }
            });
          }

          const clientHeight = rect.height;
          const mergeCol = allBricks[index]?.mergeCol || 1;
          const brickWidth = columnSize * mergeCol + gutter * (mergeCol - 1);

          const newRect = {
            column: columNum,
            left: columNum * (columnSize + gutter) + leftOffset,
            top: minHeight,
            height: clientHeight,
            width: brickWidth,
            bottom: minHeight + clientHeight,
            right: columNum * (columnSize + gutter) + brickWidth + leftOffset,
            isBanner,
            mergeCol,
          };

          // 更新已经渲染数据
          newMap.set(key, newRect);

          if (isBanner) {
            if (isFirstColumn) {
              // 特殊Banner（如同在画板）：从第一列开始，手动更新高度
              for (let i = 0; i < allBricks[index]?.mergeCol; i++) {
                if (i < maxColumn) {
                  columnHeightArrCurrent[i] =
                    minHeight + (clientHeight > 0 ? clientHeight + gutter : 0);
                }
              }
            } else {
              // 普通Banner：使用原有的填充逻辑
              createFillBrickLogic({
                brickId,
                columnSize,
                gutter,
                maxColumn,
                columnHeightArrCurrent,
                bricks: allBricks,
                brickToDomMap: null,
                clientHeight,
                minHeight,
                mergeCol: allBricks[index]?.mergeCol,
                excludeIndex: index,
              });
            }
          } else {
            // 普通卡片：只更新当前列的高度
            columnHeightArrCurrent[columNum] =
              minHeight +
              (clientHeight > 0 // clientHeight没有高度时，不增加 gutter 高度
                ? clientHeight + gutter
                : 0);

            if (allBricks[index]?.mergeCol) {
              // 给横跨的列都添加高度
              for (let i = 1; i < allBricks[index]?.mergeCol; i++) {
                // 不超过最大列数
                if (columNum + i < maxColumn) {
                  columnHeightArrCurrent[columNum + i] +=
                    clientHeight > 0 ? clientHeight + gutter : 0;
                }
              }
            }
          }
        }
      }

      computedBricks.current = newMap;
    }, [
      columnNum,
      columnSize,
      gutter,
      brickId,
      allBricks,
      createFillBrickLogic,
    ]);

    // 重新渲染整个瀑布流所有内容
    const relayout = useCallback(
      (isReCompute) => {
        if (typeof window === "undefined") {
          return;
        }

        // 确保 columnHeightArr 的长度与 columnNum 一致
        if (columnHeightArr.current.length !== columnNum) {
          columnHeightArr.current = Array(columnNum).fill(0);
        } else {
          columnHeightArr.current.fill(0);
        }

        if (isReCompute) {
          // 元素高度没有变化，只是重排，秩序重新计算布局即可
          recompute();
        } else {
          // 元素高度有变化，清空布局
          computedBricks.current.clear();
          fillBricksMap.current.clear();
          containerScrollHeight.current = 0;
        }

        // @TODO: 临时解决列数变化情况，触发滚动，重新布局
        setScrollTop((scrollTop) => scrollTop + 1);
      },
      [columnNum, recompute]
    );

    // 列数量、尺寸发生变化，需要同步执行relayout，不能异步（useEffect），不然会导致计算延迟而混乱
    useMemo(() => {
      // 当 columnNum 变化时，立即重置相关状态
      if (columnHeightArr.current.length !== columnNum) {
        columnHeightArr.current = Array(columnNum).fill(0);
        // 列数变化时，清空所有缓存，强制重新计算
        computedBricks.current.clear();
        fillBricksMap.current.clear();
        containerScrollHeight.current = 0;
      }
      relayout();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [relayout, columnSize, columnNum]);

    // 监听容器宽度变化，触发重新布局
    useEffect(() => {
      if (containerHeight > 0) {
        relayout();
      }
    }, [containerHeight, relayout]);

    // 添加对 columnNum 变化的专门监听
    useEffect(() => {
      // 当 columnNum 变化时，确保立即重置状态
      if (columnHeightArr.current.length !== columnNum) {
        columnHeightArr.current = Array(columnNum).fill(0);
        computedBricks.current.clear();
        fillBricksMap.current.clear();
        containerScrollHeight.current = 0;
        // 强制触发重新布局
        relayout();
      }
    }, [columnNum, relayout]);

    useEventListener(EVENT.MASONRY_RELAYOUT, (event) => {
      relayout(event.detail?.isReCompute);
    });

    // 可视区域坐标
    const visibleRect = useMemo(() => {
      const expandSize =
        threshold > 10 ? threshold : containerHeight * threshold;
      const top = scrollTop - containerOffsetTop - expandSize;

      const bottom =
        scrollTop + containerHeight - containerOffsetTop + expandSize;

      return { top, bottom };
    }, [threshold, containerHeight, containerOffsetTop, scrollTop]);

    // 需要被渲染的brick
    const renderBricks = useMemo(() => {
      // 服务端渲染
      if (typeof window === "undefined") {
        return allBricks;
      }

      const bricks = allBricks;
      const keys = {}; // 避免重复
      const beRenders = [];

      let unRect = 0; // 未定位过的元素个数
      for (let i = 0; i < bricks.length; i++) {
        const brick = bricks[i];
        const id = brick[brickId];
        const rect = computedBricks.current.get(id);
        if (rect) {
          if (rect.bottom > visibleRect.top && rect.top < visibleRect.bottom) {
            // 视口范围内
            if (!keys[id]) {
              beRenders.push(brick);
              keys[id] = true;
            }
          }
        } else {
          // 找到没有加载过的brick
          if (!keys[id]) {
            beRenders.push(brick);
            keys[id] = true;
            unRect += 1;
          }
        }
      }

      // 从单独的fillBricksMap中添加填充元素到渲染列表
      const fillBricksMapRef = fillBricksMap.current;
      for (let [key, rect] of fillBricksMapRef) {
        // 填充元素一旦创建就稳定显示，不受 visibleRect 影响
        const originalBrick = bricks.find(
          (brick) => brick[brickId] === rect.originalBrickId
        );
        if (originalBrick && !keys[key]) {
          beRenders.push({
            ...originalBrick,
            [brickId]: key, // 使用特殊的 fillBrick ID
            isFillBrick: true,
            // 保留原始的 pin_id 用于跳转链接
            originalPinId: originalBrick.pin_id || originalBrick[brickId],
          });
          keys[key] = true;
        }
      }

      return beRenders;
    }, [allBricks, brickId, visibleRect, fillBricksMap]);

    const runFlagRef = useRef(false);
    // 定位所有Dom节点
    useEffect(() => {
      function positionBrick() {
        const container = containerRef.current;
        const maxColumn = columnNum;

        // safari兼容性处理
        if (!container) {
          return;
        }

        const brickDom = [...(container.children || [])];

        // 创建 brick 数据到 DOM 元素的映射
        const brickToDomMap = new Map();
        brickDom.forEach((dom, index) => {
          if (dom.id === "loading") return;
          if (renderBricks[index]) {
            brickToDomMap.set(renderBricks[index][brickId], dom);
          }
        });

        const computedBrickMap = computedBricks.current;
        const columnHeightArrCurrent = columnHeightArr.current;
        let unRect = 0;
        let outVisibleRect = false;
        brickDom.forEach((brick, index) => {
          // 忽略最后一个loading子节点
          if (brick.id === "loading" || !renderBricks[index]) {
            return;
          }

          if (renderBricks[index]?.isFillBrick) {
            return;
          }

          const rect = computedBrickMap.get(renderBricks[index]?.[brickId]);

          if (!rect) {
            let columNum = 0;
            let minHeight = Infinity;

            const isBanner = renderBricks[index]?.isBanner;
            const isFirstColumn = renderBricks[index]?.isFirstColumn;

            if (isBanner && isFirstColumn) {
              // 【同在画板】等特殊Banner：第一列插入，但使用Banner的高度逻辑避免重叠
              minHeight = Math.max(...columnHeightArrCurrent);
              columNum = 0;
            } else if (isBanner) {
              // 普通Banner：从最右边开始
              minHeight = Math.max(...columnHeightArrCurrent);
              columNum = maxColumn - renderBricks[index]?.mergeCol;
            } else if (isFirstColumn) {
              // 普通卡片强制插入到第一列
              columNum = 0;
              minHeight = columnHeightArrCurrent[0];
            } else {
              // 普通卡片：获取最小的列
              columnHeightArrCurrent.forEach((value, i) => {
                if (value < minHeight) {
                  columNum = i;
                  minHeight = value;
                }
              });
            }

            const clientHeight = Math.floor(brick.clientHeight);
            const mergeCol = renderBricks[index]?.mergeCol || 1;
            const brickWidth = columnSize * mergeCol + gutter * (mergeCol - 1);

            const rect = {
              column: columNum,
              left: columNum * (columnSize + gutter) + leftOffset,
              top: minHeight,
              height: clientHeight,
              width: brickWidth,
              bottom: minHeight + clientHeight,
              right: columNum * (columnSize + gutter) + brickWidth + leftOffset,
              isBanner,
              mergeCol,
            };

            if (
              (!renderBricks[index]?.isFillBrick &&
                rect.bottom > visibleRect.top &&
                rect.top < visibleRect.bottom) ||
              !visibleRect.top
            ) {
              // 视口范围内
              Promise.resolve().then(() => {
                brick.classList.add(styles.show);
                brick.style.transform = `translate(${rect.left}px, ${rect.top}px)`;
              });
            }

            // 更新已经渲染数据
            computedBrickMap.set(renderBricks[index]?.[brickId], rect);

            // 更新已计算的高度
            if (isBanner) {
              if (isFirstColumn) {
                // 特殊Banner（如同在画板）：从第一列开始，手动更新高度
                for (let i = 0; i < renderBricks[index]?.mergeCol; i++) {
                  if (i < maxColumn) {
                    columnHeightArrCurrent[i] =
                      minHeight +
                      (clientHeight > 0 ? clientHeight + gutter : 0);
                  }
                }
              } else {
                // 普通Banner：使用原有的填充逻辑
                createFillBrickLogic({
                  brickId,
                  columnSize,
                  gutter,
                  maxColumn,
                  columnHeightArrCurrent,
                  bricks: renderBricks,
                  brickToDomMap,
                  clientHeight,
                  minHeight,
                  mergeCol: renderBricks[index]?.mergeCol,
                  excludeIndex: index,
                });
              }
            } else {
              // 普通卡片：只更新当前列的高度
              columnHeightArrCurrent[columNum] =
                minHeight +
                (clientHeight > 0 // clientHeight没有高度时，不增加 gutter 高度
                  ? clientHeight + gutter
                  : 0);

              if (renderBricks[index]?.mergeCol) {
                // 给横跨的列都添加高度
                for (let i = 1; i < renderBricks[index]?.mergeCol; i++) {
                  // 不超过最大列数
                  if (columNum + i < maxColumn) {
                    columnHeightArrCurrent[columNum + i] +=
                      clientHeight > 0 ? clientHeight + gutter : 0;
                  }
                }
              }
            }

            unRect += 1;
            if (rect.top > visibleRect.bottom) {
              outVisibleRect = true;
            }
          }
        });

        // 计算容器高度时，只使用computedBrickMap中的元素，不包含填充元素
        const heights = [];
        for (let [key, rect] of computedBrickMap) {
          heights.push(rect.bottom);
        }
        // 如果没有元素，使用当前列高度
        const maxHeight =
          heights.length > 0
            ? Math.max(...heights)
            : Math.max(...columnHeightArr.current);
        containerScrollHeight.current = Math.max(
          maxHeight,
          containerScrollHeight.current
        );
        container.style.height = containerScrollHeight.current + "px";

        // 加载更多未定位brick
        if (unRect >= 20 && !outVisibleRect) {
          setScrollTop((scrollTop) => scrollTop + 1);
        }

        // 定位完成后回调函数
        onRendered();
        loadingRef.current = false;
      }

      // Safari浏览器渲染问题，Safari会在css没有应用时，就执行这里的代码，导致获取dom的宽高尺寸等信息不对，继而导致瀑布流展示异常，所以需要通过异步解决这个问题
      // 通过runFlagRef控制，只有第一次执行时需要异步，后续执行时直接执行
      if (isSafari() && !runFlagRef.current) {
        runFlagRef.current = true;
        requestAnimationFrame(positionBrick);
      } else {
        positionBrick();
      }
    }, [
      renderBricks,
      visibleRect,
      columnSize,
      gutter,
      brickId,
      onRendered,
      columnNum,
      createFillBrickLogic,
      leftOffset,
    ]);

    // 滚动事件
    useEffect(() => {
      // 没有提供 scrollElement 的话，绑定window的滚动事件
      let target =
        typeof scrollElement === "string"
          ? document.getElementById(scrollElement)
          : scrollElement || window;

      const change = debounce(
        () => {
          if (isUnmount.current) {
            return;
          }

          // 获取真实 DOM是scrollTop， window是scrollY
          setScrollTop(target.scrollTop || target.scrollY || 0);
        },
        100,
        { trailing: true, maxWait: 200, leading: false }
      );

      // 初始化设置
      change();

      // 绑定滚动事件
      target.addEventListener("scroll", change, { passive: true });

      // 取消滚动事件
      return () => {
        target.removeEventListener("scroll", change, { passive: true });
      };
    }, [scrollElement]);

    useImperativeHandle(ref, () => ({
      // 获取所有图片的位置，用来做框选操作
      getBricksPosition: () => {
        return {
          containerOffsetTop:
            containerRef.current?.getBoundingClientRect()?.top,
          containerOffsetLeft:
            containerRef.current?.getBoundingClientRect()?.left,
          computedBricks,
        };
      },
      relayout,
    }));

    // 创建瀑布流预布局script函数
    const createMasonryPreLayoutScript = useCallback(() => {
      return `
        function preLayoutMasonry() {
          // 查找瀑布流容器
          const masonryContainer = document.querySelector('.masonry');
          if (!masonryContainer) {
            return;
          }
          
          // 获取所有瀑布流卡片（除了loading元素）
          const allBricks = Array.from(masonryContainer.children).filter(child => 
            !child.id || child.id !== 'loading'
          );
          
          if (allBricks.length === 0) {
            return;
          }
          
          // 检测是否是详情页并独立计算参数
          const isDetailPage = window.location.pathname.includes('/pins/');
          let columnNum, columnSize;
          const gutter = ${gutter};
          
          if (isDetailPage) {
            // 详情页：优先使用FullPinList传入的参数（已考虑SideNav宽度）
            // 如果有传入参数，直接使用；否则自己计算
            const hasCustomParams = ${columnNum} > 0 && ${columnSize} > 0;
            
            if (hasCustomParams) {
              // 使用FullPinList计算好的参数（已考虑SideNav伸缩）
              columnNum = ${columnNum};
              columnSize = ${columnSize};
            } else {
              // 兜底：如果没有传入参数，使用window.innerWidth计算
              const windowWidth = window.innerWidth;
              const containerWidth = windowWidth - 24 * 2 - (52 + 20) * 2;
              const benchWidth = 240;
              const itemGap = 16;
              
              let baseColumns = Math.floor((containerWidth - itemGap) / (benchWidth + itemGap));
              const userMaxCol = 9;
              let actualColumnNum = Math.min(baseColumns, userMaxCol);
              actualColumnNum = Math.max(2, actualColumnNum);
              
              const displayWidth = actualColumnNum * (benchWidth + itemGap) - itemGap;
              const diff = containerWidth - displayWidth;
              
              let calculatedColumnSize = benchWidth;
              if (actualColumnNum === userMaxCol) {
                calculatedColumnSize = benchWidth;
              } else if (diff > benchWidth / 2 && actualColumnNum < userMaxCol) {
                actualColumnNum = actualColumnNum + 1;
                calculatedColumnSize = (containerWidth + itemGap) / actualColumnNum - itemGap;
              } else {
                calculatedColumnSize = (containerWidth + itemGap) / actualColumnNum - itemGap;
              }
              
              columnNum = actualColumnNum;
              columnSize = calculatedColumnSize;
            }
          } else {
            // 非详情页使用传入参数
            columnNum = ${columnNum};
            columnSize = ${columnSize};
          }
          
          // 初始化列高度数组
          const columnHeights = new Array(columnNum).fill(0);
          
          // 布局每个卡片
          allBricks.forEach((brick, index) => {
            // 获取卡片的列占用数
            let mergeCol = 1;
            
            // 通过data属性识别特殊卡片
            const pinType = brick.querySelector('[data-pin-type]')?.getAttribute('data-pin-type');
            if (pinType === 'pinDetailInfo') {
              // 详情卡片 - 根据列数计算占用列数
              if (columnNum >= 6) {
                mergeCol = Math.min(Math.floor(columnNum * 0.6), columnNum - 1);
              } else {
                mergeCol = Math.min(Math.floor(columnNum * 0.8), columnNum);
              }
            }
            
            // 找到最低的列
            let minHeight = columnHeights[0];
            let columnIndex = 0;
            
            for (let i = 1; i <= columnNum - mergeCol; i++) {
              if (columnHeights[i] < minHeight) {
                minHeight = columnHeights[i];
                columnIndex = i;
              }
            }
            
            // 获取卡片高度
            let elementHeight = 300; // 默认高度
            
            // 尝试从现有样式或内容获取高度
            if (brick.offsetHeight > 0) {
              elementHeight = brick.offsetHeight;
            } else {
              // 查找内部元素的高度
              const innerElement = brick.querySelector('[style*="height"]');
              if (innerElement) {
                const heightMatch = innerElement.style.height.match(/(\\d+)/);
                if (heightMatch) {
                  elementHeight = parseInt(heightMatch[1]);
                }
              }
            }
            
            // 设置位置
            const left = columnIndex * (columnSize + gutter) + ${leftOffset};
            const top = minHeight;
            
            // 应用样式（只设置位置，避免与React组件冲突）
            brick.style.position = 'absolute';
            // 更新列高度
            for (let i = columnIndex; i < columnIndex + mergeCol; i++) {
              if (i < columnNum) {
                columnHeights[i] = top + elementHeight + gutter;
              }
            }
          });
          
          // 设置容器高度
          const maxHeight = Math.max(...columnHeights);
          masonryContainer.style.height = maxHeight + 'px';
        }
        
        // 执行预布局
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', preLayoutMasonry);
        } else {
          setTimeout(preLayoutMasonry, 0); // 延迟执行，确保DOM已完成
        }
      `;
    }, [columnNum, columnSize, gutter, leftOffset]);

    return (
      <>
        {/* 只在服务端渲染和首次客户端渲染时执行预布局脚本 */}
        {typeof window === "undefined" && (
          <Script
            id="masonry-pre-layout-script"
            strategy="beforeInteractive"
            dangerouslySetInnerHTML={{ __html: createMasonryPreLayoutScript() }}
          />
        )}

        <div
          className={classNames([
            styles.masonry,
            className,
            isDragging && styles.isDragging,
          ])}
          style={{
            gridAutoColumns: `${columnSize}px`,
            gap: `${gutter}px`,
            ...style,
          }}
          ref={containerRef}
        >
          {renderBricks.map(renderChild)}
          {loadingRef.current && (
            <div id="loading" className={styles.loading}>
              {loadingContent}
            </div>
          )}
        </div>
      </>
    );
  }
);
